// Eskom PARCC — JSON backup builder (pure, deterministic).
//
// The monolith's `exportBackup` dumped its localStorage state (summary +
// findings + added/edited transactions). The SQL-native rebuild instead writes a
// clean, typed SNAPSHOT of the four read collections — transactions, findings,
// CES rows and working papers — exactly as the typed read layer (src/lib/db)
// surfaces them. That is the faithful, useful backup of the live data: it
// restores/round-trips without the monolith's sheet-header `raw{}` map.
//
// The `generatedAt` timestamp is passed IN by the caller (the download glue),
// never read from Date.now() here — so the builder is deterministic and
// unit-testable. No DOM, no Blob.
import type { Transaction } from '../../data/types';
import type { FindingRead, CesRowRead, WorkingPaperRead } from '../../lib/db';

/** The four typed read collections that make up a backup snapshot. */
export interface BackupSnapshot {
  transactions: readonly Transaction[];
  findings: readonly FindingRead[];
  cesRows: readonly CesRowRead[];
  workingPapers: readonly WorkingPaperRead[];
}

/** The serialised backup document: the snapshot stamped with its generation time. */
export interface BackupDocument extends BackupSnapshot {
  generatedAt: string;
}

/**
 * Serialise a backup snapshot to pretty-printed JSON, stamped with the supplied
 * ISO timestamp. `generatedAt` is passed in (the caller owns the clock), so the
 * output is deterministic for a given snapshot + timestamp. Round-trips via
 * `JSON.parse` to a {@link BackupDocument}.
 */
export function buildBackupJson(generatedAt: string, snapshot: BackupSnapshot): string {
  const doc: BackupDocument = {
    generatedAt,
    transactions: snapshot.transactions,
    findings: snapshot.findings,
    cesRows: snapshot.cesRows,
    workingPapers: snapshot.workingPapers,
  };
  return JSON.stringify(doc, null, 2);
}
